import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearCart } from '../store/cartSlice';
import { FiCheck, FiChevronRight, FiPlus, FiMapPin, FiCreditCard, FiSmartphone, FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import './Checkout.css';

const STEPS = ['BAG', 'ADDRESS', 'PAYMENT', 'SUMMARY'];

export default function Checkout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const items = useSelector(s => s.cart.items);
  const { user } = useSelector(s => s.auth);

  const [step, setStep] = useState(1);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [cardDetails, setCardDetails] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [upiId, setUpiId] = useState('');
  const [placing, setPlacing] = useState(false);
  const [addresses, setAddresses] = useState([
    { id: 1, name: user?.name || 'John Doe', phone: '9876543210', address: '123 MG Road', city: 'Bangalore', state: 'Karnataka', pincode: '560001', type: 'HOME' },
  ]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ name: '', phone: '', address: '', city: '', state: '', pincode: '', type: 'HOME' });

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const originalTotal = items.reduce((sum, i) => sum + (i.originalPrice || i.price) * i.quantity, 0);
  const discount = originalTotal - subtotal;
  const delivery = subtotal >= 499 ? 0 : 49;
  const total = subtotal + delivery;

  const handleAddAddress = () => {
    if (!newAddress.name || !newAddress.phone || !newAddress.address || !newAddress.city || !newAddress.pincode) {
      toast.error('Please fill all required fields'); return;
    }
    const addr = { ...newAddress, id: Date.now() };
    setAddresses(prev => [...prev, addr]);
    setSelectedAddress(addr);
    setShowAddAddress(false);
    setNewAddress({ name: '', phone: '', address: '', city: '', state: '', pincode: '', type: 'HOME' });
    toast.success('Address added!');
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) { toast.error('Please select a delivery address'); return; }
    if (!paymentMethod) { toast.error('Please select a payment method'); return; }
    if (paymentMethod === 'CARD' && (!cardDetails.number || !cardDetails.expiry || !cardDetails.cvv || !cardDetails.name)) {
      toast.error('Please fill card details'); return;
    }
    if (paymentMethod === 'UPI' && !upiId) { toast.error('Please enter UPI ID'); return; }

    setPlacing(true);
    try {
      const orderData = {
        items: items.map(i => ({ productId: i.id, quantity: i.quantity, size: i.selectedSize, price: i.price })),
        addressId: selectedAddress.id, paymentMethod, totalAmount: total,
      };
      await api.post('/api/orders', orderData);
      dispatch(clearCart());
      toast.success('Order placed successfully! 🎉');
      navigate('/orders');
    } catch {
      // For demo without backend
      dispatch(clearCart());
      toast.success('Order placed successfully! 🎉');
      navigate('/orders');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="checkout-page">
      {/* Progress Bar */}
      <div className="checkout-progress">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`progress-step ${i < step ? 'done' : i === step ? 'active' : ''}`}>
              <div className="progress-step-icon">
                {i < step ? <FiCheck /> : i + 1}
              </div>
              <span>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`progress-line ${i < step ? 'done' : ''}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="checkout-layout">
        {/* Left: Steps */}
        <div className="checkout-main">

          {/* Step 1: Address */}
          <div className={`checkout-step-card ${step === 1 ? 'active-step' : ''}`}>
            <div className="step-header" onClick={() => step > 1 && setStep(1)}>
              {step > 1 ? <span className="step-done-icon"><FiCheck /></span> : <span className="step-num">1</span>}
              <h3>DELIVERY ADDRESS</h3>
              {step > 1 && <button className="step-change-btn">CHANGE</button>}
            </div>

            {step === 1 && (
              <div className="step-content">
                <div className="addresses-list">
                  {addresses.map(addr => (
                    <label key={addr.id} className={`address-card${selectedAddress?.id === addr.id ? ' selected' : ''}`}>
                      <input type="radio" name="address" checked={selectedAddress?.id === addr.id} onChange={() => setSelectedAddress(addr)} />
                      <div className="address-info">
                        <div className="address-header-row">
                          <span className="addr-name">{addr.name}</span>
                          <span className="addr-type-badge">{addr.type}</span>
                          <span className="addr-phone">{addr.phone}</span>
                        </div>
                        <p className="addr-full">{addr.address}, {addr.city}, {addr.state} - {addr.pincode}</p>
                      </div>
                    </label>
                  ))}

                  {/* Add New Address */}
                  <div className={`add-address-toggle ${showAddAddress ? 'open' : ''}`}>
                    <button className="add-address-btn" onClick={() => setShowAddAddress(!showAddAddress)}>
                      <FiPlus /> Add New Address
                    </button>
                    {showAddAddress && (
                      <div className="new-address-form">
                        <div className="form-row-2">
                          <div className="input-group">
                            <label>Full Name *</label>
                            <input value={newAddress.name} onChange={e => setNewAddress(p => ({ ...p, name: e.target.value }))} placeholder="Enter full name" />
                          </div>
                          <div className="input-group">
                            <label>Phone Number *</label>
                            <input value={newAddress.phone} onChange={e => setNewAddress(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile number" maxLength={10} />
                          </div>
                        </div>
                        <div className="input-group">
                          <label>Address (House No, Building, Street) *</label>
                          <input value={newAddress.address} onChange={e => setNewAddress(p => ({ ...p, address: e.target.value }))} placeholder="Enter full address" />
                        </div>
                        <div className="form-row-3">
                          <div className="input-group">
                            <label>City *</label>
                            <input value={newAddress.city} onChange={e => setNewAddress(p => ({ ...p, city: e.target.value }))} placeholder="City" />
                          </div>
                          <div className="input-group">
                            <label>State</label>
                            <input value={newAddress.state} onChange={e => setNewAddress(p => ({ ...p, state: e.target.value }))} placeholder="State" />
                          </div>
                          <div className="input-group">
                            <label>Pincode *</label>
                            <input value={newAddress.pincode} onChange={e => setNewAddress(p => ({ ...p, pincode: e.target.value.replace(/\D/, '').slice(0, 6) }))} placeholder="6-digit pincode" maxLength={6} />
                          </div>
                        </div>
                        <div className="address-type-row">
                          {['HOME', 'WORK', 'OTHER'].map(t => (
                            <button key={t} className={`addr-type-btn${newAddress.type === t ? ' active' : ''}`} onClick={() => setNewAddress(p => ({ ...p, type: t }))}>
                              {t === 'HOME' ? '🏠' : t === 'WORK' ? '🏢' : '📍'} {t}
                            </button>
                          ))}
                        </div>
                        <button className="btn btn-primary" onClick={handleAddAddress}>Save Address</button>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  className="step-continue-btn"
                  disabled={!selectedAddress}
                  onClick={() => { if (selectedAddress) setStep(2); else toast.error('Please select an address'); }}
                >
                  DELIVER HERE <FiChevronRight />
                </button>
              </div>
            )}
          </div>

          {/* Step 2: Payment */}
          <div className={`checkout-step-card ${step === 2 ? 'active-step' : step > 2 ? '' : 'disabled-step'}`}>
            <div className="step-header" onClick={() => step > 2 && setStep(2)}>
              {step > 2 ? <span className="step-done-icon"><FiCheck /></span> : <span className="step-num">2</span>}
              <h3>PAYMENT OPTIONS</h3>
              {step > 2 && <button className="step-change-btn">CHANGE</button>}
            </div>

            {step === 2 && (
              <div className="step-content">
                <div className="payment-options">
                  {/* UPI */}
                  <div className={`payment-option-card${paymentMethod === 'UPI' ? ' selected' : ''}`}>
                    <label className="payment-option-label">
                      <input type="radio" name="payment" value="UPI" checked={paymentMethod === 'UPI'} onChange={() => setPaymentMethod('UPI')} />
                      <div className="payment-option-info">
                        <FiSmartphone />
                        <div>
                          <strong>UPI</strong>
                          <p>Pay using any UPI app</p>
                        </div>
                      </div>
                    </label>
                    {paymentMethod === 'UPI' && (
                      <div className="payment-details">
                        <div className="upi-apps">
                          {['GPay', 'PhonePe', 'Paytm', 'BHIM'].map(app => (
                            <div key={app} className="upi-app-btn">{app}</div>
                          ))}
                        </div>
                        <div className="input-group" style={{ marginTop: 12 }}>
                          <label>Enter UPI ID</label>
                          <input value={upiId} onChange={e => setUpiId(e.target.value)} placeholder="yourname@upi" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Credit/Debit Card */}
                  <div className={`payment-option-card${paymentMethod === 'CARD' ? ' selected' : ''}`}>
                    <label className="payment-option-label">
                      <input type="radio" name="payment" value="CARD" checked={paymentMethod === 'CARD'} onChange={() => setPaymentMethod('CARD')} />
                      <div className="payment-option-info">
                        <FiCreditCard />
                        <div>
                          <strong>Credit / Debit Card</strong>
                          <p>Visa, Mastercard, RuPay and more</p>
                        </div>
                      </div>
                    </label>
                    {paymentMethod === 'CARD' && (
                      <div className="payment-details">
                        <div className="input-group">
                          <label>Card Number</label>
                          <input value={cardDetails.number} onChange={e => setCardDetails(p => ({ ...p, number: e.target.value.replace(/\D/, '').slice(0, 16) }))} placeholder="1234 5678 9012 3456" maxLength={16} />
                        </div>
                        <div className="input-group">
                          <label>Cardholder Name</label>
                          <input value={cardDetails.name} onChange={e => setCardDetails(p => ({ ...p, name: e.target.value }))} placeholder="Name on card" />
                        </div>
                        <div className="form-row-2">
                          <div className="input-group">
                            <label>Expiry (MM/YY)</label>
                            <input value={cardDetails.expiry} onChange={e => setCardDetails(p => ({ ...p, expiry: e.target.value }))} placeholder="MM/YY" maxLength={5} />
                          </div>
                          <div className="input-group">
                            <label>CVV</label>
                            <input type="password" value={cardDetails.cvv} onChange={e => setCardDetails(p => ({ ...p, cvv: e.target.value.replace(/\D/, '').slice(0, 3) }))} placeholder="***" maxLength={3} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Net Banking */}
                  <div className={`payment-option-card${paymentMethod === 'NETBANKING' ? ' selected' : ''}`}>
                    <label className="payment-option-label">
                      <input type="radio" name="payment" value="NETBANKING" checked={paymentMethod === 'NETBANKING'} onChange={() => setPaymentMethod('NETBANKING')} />
                      <div className="payment-option-info">
                        <span style={{ fontSize: 22 }}>🏦</span>
                        <div>
                          <strong>Net Banking</strong>
                          <p>All major banks supported</p>
                        </div>
                      </div>
                    </label>
                    {paymentMethod === 'NETBANKING' && (
                      <div className="payment-details">
                        <div className="bank-list">
                          {['HDFC Bank', 'SBI', 'ICICI Bank', 'Axis Bank', 'Kotak Bank'].map(bank => (
                            <div key={bank} className="bank-option">{bank}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* COD */}
                  <div className={`payment-option-card${paymentMethod === 'COD' ? ' selected' : ''}`}>
                    <label className="payment-option-label">
                      <input type="radio" name="payment" value="COD" checked={paymentMethod === 'COD'} onChange={() => setPaymentMethod('COD')} />
                      <div className="payment-option-info">
                        <FiDollarSign />
                        <div>
                          <strong>Cash on Delivery</strong>
                          <p>Pay when your order arrives</p>
                        </div>
                      </div>
                    </label>
                    {paymentMethod === 'COD' && (
                      <div className="payment-details cod-info">
                        <p>✅ No extra charges for Cash on Delivery</p>
                        <p>📦 Pay at the time of delivery</p>
                        <p>↩️ Easy returns within 30 days</p>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  className="step-continue-btn"
                  disabled={!paymentMethod}
                  onClick={() => { if (paymentMethod) setStep(3); else toast.error('Please select payment method'); }}
                >
                  CONTINUE <FiChevronRight />
                </button>
              </div>
            )}
          </div>

          {/* Step 3: Order Summary */}
          <div className={`checkout-step-card ${step === 3 ? 'active-step' : 'disabled-step'}`}>
            <div className="step-header">
              <span className="step-num">3</span>
              <h3>ORDER SUMMARY</h3>
            </div>
            {step === 3 && (
              <div className="step-content">
                <div className="order-summary-items">
                  {items.map(item => (
                    <div key={item.cartId} className="os-item">
                      <span className="os-emoji">{item.imageEmoji || '👗'}</span>
                      <div className="os-info">
                        <p className="os-brand">{item.brand}</p>
                        <p className="os-name">{item.name}</p>
                        <p className="os-meta">Size: {item.selectedSize} | Qty: {item.quantity}</p>
                      </div>
                      <span className="os-price">₹{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="os-delivery-info">
                  <FiMapPin />
                  <div>
                    <strong>Delivering to: {selectedAddress?.name}</strong>
                    <p>{selectedAddress?.address}, {selectedAddress?.city} - {selectedAddress?.pincode}</p>
                  </div>
                </div>
                <div className="os-payment-info">
                  <strong>Payment: </strong>{paymentMethod === 'COD' ? 'Cash on Delivery' : paymentMethod === 'CARD' ? 'Credit/Debit Card' : paymentMethod}
                </div>
                <button
                  className="place-order-final-btn"
                  onClick={handlePlaceOrder}
                  disabled={placing}
                >
                  {placing ? 'Placing Order...' : `PLACE ORDER — ₹${total.toLocaleString()}`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Price Summary Sidebar */}
        <div className="checkout-sidebar">
          <div className="price-summary-card">
            <h3>PRICE DETAILS</h3>
            <div className="summary-divider" />
            <div className="price-row"><span>Price ({items.length} items)</span><span>₹{originalTotal.toLocaleString()}</span></div>
            {discount > 0 && <div className="price-row green"><span>Discount</span><span>−₹{discount.toLocaleString()}</span></div>}
            <div className="price-row"><span>Delivery Charges</span><span className={delivery === 0 ? 'green-text' : ''}>{delivery === 0 ? 'FREE' : `₹${delivery}`}</span></div>
            <div className="summary-divider" />
            <div className="price-row total"><span>Total Amount</span><span>₹{total.toLocaleString()}</span></div>
            {discount > 0 && <p className="savings-msg">🎉 You will save ₹{discount.toLocaleString()} on this order!</p>}
          </div>
          <div className="safe-payment-card">
            <div className="safe-icon">🔒</div>
            <div>
              <strong>Safe and Secure Payments</strong>
              <p>Easy returns. 100% Authentic Products</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
